/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";
import { Image, notification } from "antd";
import HelmetCustom from "components/Common/HelmetCustom";
import Loading from "components/Common/Loading";
import {
  DataDetailCategoryType,
  LIST_DATA,
  ListDataType,
  SymbolsType,
} from "contants/list-category-game";
import { AnimatePresence, motion } from "framer-motion";
import { handleLoginGame } from "helpers/handleLoginGame";
import { useAppDispatch, useAppSelector } from "hooks/app-hook";
import _find from "lodash/find";
import NotFoundPage from "pages/Common/NotFound";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useParams } from "react-router-dom";
import { selectUsername } from "store/auth";
import { StatusNotification } from "types";

import { CategoryGameNewStyle } from "./index.style";

const initialValue = {
  id: "",
  name: "",
  symbols: [],
  data: [],
  path: "",
};

const CategoryGameNew = () => {
  const [api, contextHolder] = notification.useNotification();
  const openNotification = (status: StatusNotification, message: string) => {
    api[status]({
      message,
      description: "",
      duration: 20,
      key: "LoginGame",
    });
  };

  const { categoryName = "" } = useParams();
  const { pathname } = useLocation();

  const { t } = useTranslation("categoryGame");

  const dispatch = useAppDispatch();

  const username = useAppSelector(selectUsername);

  const [dataCategoryGame, setDataCategoryGame] =
    useState<ListDataType>(initialValue);
  const [selectedProvider, setSelectedProvider] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);
  const [isShowMore, setIsShowMore] = useState<boolean>(false);
  const [isNotFound, setIsNotFount] = useState<boolean>(false);

  useEffect(() => {
    const findData = _find(
      LIST_DATA,
      (item: ListDataType) =>
        item.name.toLowerCase() === categoryName || pathname === item.path
    );

    if (!findData) {
      setIsNotFount(true);
      return;
    }
    setDataCategoryGame(findData);
    setSelectedProvider(findData.symbols[0].providerCode);
    setIsLoading(false);

    return () => {
      setDataCategoryGame(initialValue);
    };
  }, [categoryName]);

  if (isNotFound) return <NotFoundPage />;

  if (isLoading) return <Loading height={900} center />;

  const handleOnclickEnterGame = () => {
    handleLoginGame(
      {
        game_code: "",
        name: "",
        provider_code: selectedProvider,
        type: dataCategoryGame.id,
        featured: "",
        new_arrive: "",
        picture: "",
      },
      username,
      dispatch,
      openNotification
    );
  };
  return (
    <div css={CategoryGameNewStyle.wrapper}>
      {contextHolder}
      <HelmetCustom title={dataCategoryGame.name} />
      <div css={CategoryGameNewStyle.container}>
        <div css={CategoryGameNewStyle.contentLeft.index}>
          <div css={CategoryGameNewStyle.contentLeft.header}>
            <AnimatePresence>
              {dataCategoryGame.data.map((item: DataDetailCategoryType) => (
                <motion.div
                  key={item.providerCode}
                  variants={fadeIn("right", 0.8)}
                  initial="hidden"
                  whileInView={
                    selectedProvider === item.providerCode ? "show" : "hidden"
                  }
                  css={css`
                    position: ${selectedProvider === item.providerCode
                      ? "static"
                      : "absolute"};
                  `}
                >
                  <Image
                    src={item.titleImage}
                    height={157}
                    style={{
                      objectFit: "contain",
                    }}
                    alt=""
                    preview={false}
                  />

                  <div css={CategoryGameNewStyle.contentLeft.info}>
                    <p
                      css={CategoryGameNewStyle.contentLeft.description(
                        isShowMore
                      )}
                    >
                      {t(item.description)}
                    </p>

                    <p
                      css={CategoryGameNewStyle.contentLeft.readMore(
                        selectedProvider,
                        item.providerCode
                      )}
                      onClick={() => {
                        setIsShowMore(!isShowMore);
                      }}
                    >
                      {t("Read More")}
                    </p>

                    <Image src={item.listSymbolImg} alt="" preview={false} />
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          <div
            css={css`
              margin-top: 24px;
              display: grid;
              grid-template-columns: repeat(5, 1fr);
              gap: 6px;
              max-width: 456px;
            `}
          >
            {dataCategoryGame.symbols.map((symbol: SymbolsType) =>
              selectedProvider === symbol.providerCode ? (
                <Image
                  src={symbol.iconSelected}
                  alt=""
                  preview={false}
                  key={symbol.providerCode}
                />
              ) : (
                <Image
                  src={symbol.icon}
                  alt=""
                  key={symbol.providerCode}
                  preview={false}
                  css={css`
                    cursor: ${!isDisabled ? "pointer" : "not-allowed"};
                  `}
                  onClick={() => {
                    if (isDisabled) return;

                    setSelectedProvider(symbol.providerCode);
                    setIsDisabled(true);

                    setTimeout(() => setIsDisabled(false), 1200);
                  }}
                />
              )
            )}
          </div>
          <div
            css={CategoryGameNewStyle.contentLeft.buttonEnterGame}
            onClick={handleOnclickEnterGame}
          >
            <span>{t("button:EnterGame")}</span>
          </div>
        </div>
        <div css={CategoryGameNewStyle.contentRight.index}>
          <AnimatePresence>
            {dataCategoryGame.data.map((item: DataDetailCategoryType) => (
              <motion.div
                key={item.providerCode}
                variants={fadeIn("left", 0.8)}
                initial="hidden"
                whileInView={
                  selectedProvider === item.providerCode ? "show" : "hidden"
                }
                css={css`
                  position: ${selectedProvider === item.providerCode
                    ? "static"
                    : "absolute"};
                `}
              >
                <Image src={item.image} alt="" preview={false} />
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default CategoryGameNew;

const fadeIn = (direction: string, delay: number) => {
  return {
    hidden: {
      y: direction === "up" ? 80 : direction === "down" ? -80 : 0,
      opacity: 0,
      x: direction === "left" ? 80 : direction === "right" ? -80 : 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.25, 0.25, 0.75],
      },
    },
    show: {
      x: 0,
      y: 0,
      opacity: 1,
      transition: {
        type: "tween",
        duration: 0.8,
        delay: delay,
        ease: [0.25, 0.25, 0.25, 0.75],
      },
    },
  };
};
